import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../utils/api';
import { useCommunityAuth } from '../context/CommunityAuthContext';
import { API } from '../utils/api';
import CommunityBottomNav from '../components/community/CommunityBottomNav';
import CommunityTopHeader from '../components/community/CommunityTopHeader';
import { Users, FileText, Flag, Shield, Calendar, Trash2, Ban, CheckCircle, XCircle, Eye, MapPin, ExternalLink, Zap, Inbox, Megaphone, Plus, ImagePlus, Building2 } from 'lucide-react';
import { confirmDialog } from '../components/ui/confirm-dialog';
import CommunityEventEditModal from '../components/community/CommunityEventEditModal';

const TEAL = '#4DB6AC';

export default function CommunityAdminPage() {
  const navigate = useNavigate();
  const { user, token, isAdmin } = useCommunityAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [dashboard, setDashboard] = useState(null);
  const [users_, setUsers] = useState([]);
  const [posts, setPosts] = useState([]);
  const [flaggedPosts, setFlaggedPosts] = useState([]);
  const [events, setEvents] = useState([]);
  const [editingEvent, setEditingEvent] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [attendees, setAttendees] = useState([]);
  const [completions, setCompletions] = useState([]);
  const [completionEvent, setCompletionEvent] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectingUserId, setRejectingUserId] = useState(null);
  const [challenges, setChallenges] = useState([]);
  const [challengeCompletions, setChallengeCompletions] = useState([]);
  const [completionChallenge, setCompletionChallenge] = useState(null);
  const [challengeRejectUserId, setChallengeRejectUserId] = useState(null);
  const [challengeRejectReason, setChallengeRejectReason] = useState('');
  const [pendingActions, setPendingActions] = useState([]);
  const [pendingRejectId, setPendingRejectId] = useState(null);
  const [pendingRejectReason, setPendingRejectReason] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedChecked, setSelectedChecked] = useState([]);
  const [bulkRejectReason, setBulkRejectReason] = useState('');
  const [showBulkReject, setShowBulkReject] = useState(false);
  const [allChallenges, setAllChallenges] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [showCreateAnn, setShowCreateAnn] = useState(false);
  const [annForm, setAnnForm] = useState({ title: '', description: '', image_url: '' });
  const [annSubmitting, setAnnSubmitting] = useState(false);
  const [annImageFile, setAnnImageFile] = useState(null);
  const [annImagePreview, setAnnImagePreview] = useState(null);
  // Organiser approval state — three buckets, one sub-tab at a time.
  const [organiserSubTab, setOrganiserSubTab] = useState('pending');
  const [organisers, setOrganisers] = useState({ pending: [], approved: [], rejected: [] });
  const [organiserActionLoading, setOrganiserActionLoading] = useState(null);

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    if (!isAdmin) { navigate('/community'); return; }
    fetchDashboard();
  }, [isAdmin]);

  const fetchDashboard = async () => {
    try {
      const [dashRes, usersRes, postsRes, flaggedRes, eventsRes, challengesRes, pendingRes, allChRes, annRes] = await Promise.all([
        axios.get(`${API}/community/admin/dashboard`, authHeaders),
        axios.get(`${API}/community/admin/users`, authHeaders),
        axios.get(`${API}/community/posts`, authHeaders),
        axios.get(`${API}/community/admin/flagged-posts`, authHeaders),
        axios.get(`${API}/community/events`, authHeaders).catch(() => ({ data: [] })),
        axios.get(`${API}/community/challenges`, authHeaders).catch(() => ({ data: [] })),
        axios.get(`${API}/community/admin/pending-actions`, authHeaders).catch(() => ({ data: [] })),
        axios.get(`${API}/community/admin/all-challenges`, authHeaders).catch(() => ({ data: [] })),
        axios.get(`${API}/community/announcements`, authHeaders).catch(() => ({ data: [] })),
      ]);
      setDashboard(dashRes.data);
      setUsers(usersRes.data);
      setPosts(postsRes.data);
      setFlaggedPosts(flaggedRes.data);
      setEvents(eventsRes.data);
      setChallenges(challengesRes.data);
      setAllChallenges(allChRes.data);
      setPendingActions(pendingRes.data);
      setAnnouncements(annRes.data || []);
    } catch (err) { console.error(err); }
  };

  const fetchOrganisers = async (statusKey) => {
    try {
      const res = await axios.get(`${API}/community/admin/organisers?status=${statusKey}`, authHeaders);
      setOrganisers(o => ({ ...o, [statusKey]: res.data || [] }));
    } catch (err) { console.error(err); }
  };

  // Fetch all three buckets once the Organisers tab opens, then refresh the
  // active bucket on sub-tab switches.
  useEffect(() => {
    if (activeTab !== 'organisers') return;
    ['pending', 'approved', 'rejected'].forEach(fetchOrganisers);
    // eslint-disable-next-line
  }, [activeTab]);

  const handleApproveOrganiser = async (orgUser) => {
    setOrganiserActionLoading(orgUser.id);
    try {
      await axios.post(`${API}/community/admin/organisers/${orgUser.id}/approve`, {}, authHeaders);
      await Promise.all([fetchOrganisers('pending'), fetchOrganisers('approved')]);
    } catch (err) { console.error(err); }
    setOrganiserActionLoading(null);
  };

  const handleRejectOrganiser = async (orgUser) => {
    const ok = await confirmDialog({ title: 'Reject organiser?', message: `Reject ${orgUser.organisation_name || orgUser.email}? An email will be sent.`, confirmLabel: 'Reject', danger: true });
    if (!ok) return;
    setOrganiserActionLoading(orgUser.id);
    try {
      await axios.post(`${API}/community/admin/organisers/${orgUser.id}/reject`, {}, authHeaders);
      await Promise.all([fetchOrganisers('pending'), fetchOrganisers('rejected')]);
    } catch (err) { console.error(err); }
    setOrganiserActionLoading(null);
  };

  const handleBlockUser = async (userId, blocked) => {
    try {
      await axios.post(`${API}/community/admin/users/${userId}/${blocked ? 'unblock' : 'block'}`, {}, authHeaders);
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, is_blocked: !blocked } : u));
      fetchDashboard();
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  const handleDeletePost = async (postId) => {
    if (!(await confirmDialog({ title: 'Delete post?', message: 'This will permanently remove the post.', confirmText: 'Delete', danger: true }))) return;
    try {
      await axios.delete(`${API}/community/admin/posts/${postId}`, authHeaders);
      setPosts(prev => prev.filter(p => p.id !== postId));
      setFlaggedPosts(prev => prev.filter(p => p.id !== postId));
      fetchDashboard();
    } catch {}
  };

  const handleViewAttendees = async (eventId) => {
    try {
      const res = await axios.get(`${API}/community/admin/events/${eventId}/attendees`, authHeaders);
      setSelectedEvent(res.data.event);
      setAttendees(res.data.attendees);
      setActiveTab('attendance');
    } catch (err) { alert('Failed to load attendees'); }
  };

  const handleMarkAttendance = async (eventId, userId, status) => {
    try {
      await axios.post(`${API}/community/admin/events/${eventId}/attendance/${userId}`, { status }, authHeaders);
      setAttendees(prev => prev.map(a => a.id === userId ? { ...a, attendance_status: status } : a));
    } catch {}
  };

  const handleViewCompletions = async (eventId) => {
    try {
      const res = await axios.get(`${API}/community/admin/events/${eventId}/completions`, authHeaders);
      setCompletionEvent(res.data);
      setCompletions(res.data.completions || []);
      setActiveTab('verify');
    } catch (err) { alert('Failed to load completions'); }
  };

  const handleApproveCompletion = async (userId) => {
    if (!completionEvent) return;
    try {
      await axios.post(`${API}/community/admin/events/${completionEvent.event_id}/completions/${userId}/approve`, {}, authHeaders);
      setCompletions(prev => prev.map(c => c.user_id === userId ? { ...c, status: 'approved' } : c));
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  const handleRejectCompletion = async (userId) => {
    if (!completionEvent) return;
    try {
      await axios.post(`${API}/community/admin/events/${completionEvent.event_id}/completions/${userId}/reject`,
        { reason: rejectReason || null }, authHeaders);
      setCompletions(prev => prev.map(c => c.user_id === userId ? { ...c, status: 'rejected', rejection_reason: rejectReason } : c));
      setRejectingUserId(null); setRejectReason('');
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  // ── Challenge completion handlers ──
  const handleViewChallengeCompletions = async (challengeId) => {
    try {
      const res = await axios.get(`${API}/community/admin/challenges/${challengeId}/completions`, authHeaders);
      setCompletionChallenge(res.data);
      setChallengeCompletions(res.data.completions || []);
      setActiveTab('verify-challenge');
    } catch (err) { alert('Failed to load completions'); }
  };

  const handleApproveChallengeCompletion = async (userId) => {
    if (!completionChallenge) return;
    try {
      await axios.post(`${API}/community/admin/challenges/${completionChallenge.challenge_id}/completions/${userId}/approve`, {}, authHeaders);
      setChallengeCompletions(prev => prev.map(c => c.userId === userId ? { ...c, status: 'approved' } : c));
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  const handleRejectChallengeCompletion = async (userId) => {
    if (!completionChallenge) return;
    try {
      await axios.post(`${API}/community/admin/challenges/${completionChallenge.challenge_id}/completions/${userId}/reject`,
        { reason: challengeRejectReason || null }, authHeaders);
      setChallengeCompletions(prev => prev.map(c => c.userId === userId ? { ...c, status: 'rejected', rejection_reason: challengeRejectReason } : c));
      setChallengeRejectUserId(null); setChallengeRejectReason('');
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  // ── Pending org-submitted action handlers ──
  const handleApprovePendingAction = async (challengeId) => {
    try {
      await axios.post(`${API}/community/admin/pending-actions/${challengeId}/approve`, {}, authHeaders);
      setPendingActions(prev => prev.filter(a => a.id !== challengeId));
      fetchDashboard();
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  const handleRejectPendingAction = async (challengeId) => {
    try {
      await axios.post(`${API}/community/admin/pending-actions/${challengeId}/reject`,
        { reason: pendingRejectReason || null }, authHeaders);
      setPendingActions(prev => prev.filter(a => a.id !== challengeId));
      setPendingRejectId(null); setPendingRejectReason('');
      fetchDashboard();
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  // ── User Detail handlers ──
  const handleViewUserDetail = async (userId) => {
    try {
      const res = await axios.get(`${API}/community/admin/users/${userId}/detail`, authHeaders);
      setSelectedUser(res.data);
      setActiveTab('user-detail');
    } catch (err) { alert('Failed to load user detail'); }
  };

  const handlePromoteUser = async (userId) => {
    if (!(await confirmDialog({ title: 'Promote user to admin?', message: 'This grants community admin privileges.', confirmText: 'Promote' }))) return;
    try {
      await axios.post(`${API}/community/admin/users/${userId}/promote`, {}, authHeaders);
      setSelectedUser(prev => prev ? { ...prev, role: 'COMMUNITY_ADMIN' } : prev);
      fetchDashboard();
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  const handleDemoteUser = async (userId) => {
    if (!(await confirmDialog({ title: 'Demote admin?', message: 'This revokes their admin privileges.', confirmText: 'Demote', danger: true }))) return;
    try {
      await axios.post(`${API}/community/admin/users/${userId}/demote`, {}, authHeaders);
      setSelectedUser(prev => prev ? { ...prev, role: 'COMMUNITY_USER' } : prev);
      fetchDashboard();
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  // ── Challenge soft delete toggle ──
  const handleToggleChallengeActive = async (challengeId) => {
    try {
      const res = await axios.patch(`${API}/community/admin/challenges/${challengeId}/toggle-active`, {}, authHeaders);
      setAllChallenges(prev => prev.map(c => c.id === challengeId ? { ...c, is_active: res.data.is_active } : c));
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  // ── Bulk pending actions ──
  const toggleChecked = (id) => setSelectedChecked(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const toggleAllChecked = () => {
    if (selectedChecked.length === pendingActions.length) setSelectedChecked([]);
    else setSelectedChecked(pendingActions.map(a => a.id));
  };

  const handleBulkApprove = async () => {
    if (!selectedChecked.length) return;
    try {
      await axios.post(`${API}/community/admin/pending-actions/bulk-approve`, { ids: selectedChecked }, authHeaders);
      setPendingActions(prev => prev.filter(a => !selectedChecked.includes(a.id)));
      setSelectedChecked([]); fetchDashboard();
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  const handleBulkReject = async () => {
    if (!selectedChecked.length) return;
    try {
      await axios.post(`${API}/community/admin/pending-actions/bulk-reject`, { ids: selectedChecked, reason: bulkRejectReason || null }, authHeaders);
      setPendingActions(prev => prev.filter(a => !selectedChecked.includes(a.id)));
      setSelectedChecked([]); setShowBulkReject(false); setBulkRejectReason(''); fetchDashboard();
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  const tabs = [
    { id: 'dashboard', label: 'Overview', icon: Shield },
    { id: 'users', label: 'Users', icon: Users },
    { id: 'posts', label: 'Posts', icon: FileText },
    { id: 'flagged', label: 'Flagged', icon: Flag },
    { id: 'events', label: 'Events', icon: Calendar },
    { id: 'announcements', label: 'Announce', icon: Megaphone },
    { id: 'actions', label: 'Actions', icon: Zap },
    { id: 'pending', label: `Pending${pendingActions.length > 0 ? ` (${pendingActions.length})` : ''}`, icon: Inbox },
    { id: 'organisers', label: `Organisers${organisers.pending.length > 0 ? ` (${organisers.pending.length})` : ''}`, icon: Building2 },
    { id: 'verify', label: 'Verify', icon: CheckCircle },
  ];

  const StatCard = ({ label, value, color }) => (
    <div style={{ background: '#fff', borderRadius: 12, padding: 16, border: '1px solid #F0F0F0', flex: 1, minWidth: 120 }}>
      <div style={{ fontSize: 24, fontWeight: 700, color: color || TEAL }}>{value}</div>
      <div style={{ fontSize: 12, color: '#6B7280', marginTop: 4 }}>{label}</div>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: '#F5F7FA', paddingBottom: '80px' }}>
      <CommunityTopHeader showBack />
      {editingEvent && (
        <CommunityEventEditModal event={editingEvent} token={token} onClose={() => setEditingEvent(null)}
          onSaved={(updated) => { setEvents(prev => prev.map(e => e.id === updated.id ? updated : e)); setEditingEvent(null); }} />
      )}

      {/* Tab bar */}
      <div style={{ display: 'flex', overflowX: 'auto', gap: 4, padding: '12px 16px', background: '#fff', borderBottom: '1px solid #F0F0F0' }}>
        {tabs.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '8px 14px', borderRadius: 8, border: 'none',
              fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
              background: activeTab === tab.id ? `${TEAL}15` : '#F3F4F6',
              color: activeTab === tab.id ? TEAL : '#6B7280', transition: 'all 0.2s' }}>
            <tab.icon size={14} /> {tab.label}
          </button>
        ))}
      </div>

      <div style={{ padding: '16px 16px 100px' }}>
        {/* Dashboard */}
        {activeTab === 'dashboard' && dashboard && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            <StatCard label="Total Users" value={dashboard.total_users} />
            <StatCard label="Blocked" value={dashboard.blocked_users} color="#DC2626" />
            <StatCard label="Total Posts" value={dashboard.total_posts} />
            <StatCard label="Flagged Posts" value={dashboard.flagged_posts} color="#F59E0B" />
            <StatCard label="MAD Actions" value={dashboard.total_challenges} />
            <StatCard label="Events" value={dashboard.total_events} />
          </div>
        )}

        {/* Users */}
        {activeTab === 'users' && users_.map(u => (
          <div key={u.id} onClick={() => handleViewUserDetail(u.id)} data-testid={`user-row-${u.id}`}
            style={{ background: '#fff', borderRadius: 12, padding: 14, marginBottom: 8,
              border: '1px solid #F0F0F0', display: 'flex', alignItems: 'center', gap: 12,
              opacity: u.is_blocked ? 0.6 : 1, cursor: 'pointer', transition: 'box-shadow 0.15s' }}>
            {/* Initials avatar */}
            <div style={{ width: 40, height: 40, borderRadius: '50%', background: `${TEAL}20`, color: TEAL,
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 15, flexShrink: 0 }}>
              {(u.username || u.email || '?')[0].toUpperCase()}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#1F2937', display: 'flex', alignItems: 'center', gap: 6 }}>
                {u.username}
                {u.role === 'COMMUNITY_ADMIN' && (
                  <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 4, background: '#EDE9FE', color: '#7C3AED' }}>ADMIN</span>
                )}
              </div>
              <div style={{ fontSize: 12, color: '#6B7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.email}</div>
              {u.is_blocked && <span style={{ fontSize: 11, color: '#DC2626', fontWeight: 600 }}>BLOCKED</span>}
            </div>
            <Eye size={16} color="#9CA3AF" />
          </div>
        ))}

        {/* User Detail View */}
        {activeTab === 'user-detail' && selectedUser && (
          <div>
            <button onClick={() => setActiveTab('users')}
              style={{ background: 'none', border: 'none', color: TEAL, fontSize: 13, fontWeight: 600, cursor: 'pointer', marginBottom: 16, padding: 0 }}>
              &larr; Back to Users
            </button>

            {/* Profile card */}
            <div style={{ background: '#fff', borderRadius: 14, padding: 20, border: '1px solid #F0F0F0', marginBottom: 12, textAlign: 'center' }}>
              <div style={{ width: 64, height: 64, borderRadius: '50%', background: `${TEAL}20`, color: TEAL,
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 26, margin: '0 auto 12px' }}>
                {(selectedUser.username || selectedUser.email || '?')[0].toUpperCase()}
              </div>
              <div style={{ fontSize: 18, fontWeight: 700, color: '#1F2937' }}>{selectedUser.username}</div>
              <div style={{ fontSize: 13, color: '#6B7280', marginTop: 2 }}>{selectedUser.email}</div>
              <div style={{ display: 'flex', justifyContent: 'center', gap: 6, marginTop: 8 }}>
                <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 4,
                  background: selectedUser.role === 'COMMUNITY_ADMIN' ? '#EDE9FE' : '#F3F4F6',
                  color: selectedUser.role === 'COMMUNITY_ADMIN' ? '#7C3AED' : '#6B7280' }}>
                  {selectedUser.role === 'COMMUNITY_ADMIN' ? 'Admin' : 'User'}
                </span>
                {selectedUser.is_blocked && (
                  <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 4, background: '#FEE2E2', color: '#DC2626' }}>Blocked</span>
                )}
              </div>
            </div>

            {/* Stats grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 12 }}>
              {[
                { label: 'Posts', value: selectedUser.posts_count ?? 0, color: TEAL },
                { label: 'Actions Joined', value: selectedUser.challenges_joined ?? 0, color: '#F59E0B' },
                { label: 'Social Score', value: selectedUser.social_score ?? 0, color: '#7C3AED' },
              ].map(s => (
                <div key={s.label} style={{ background: '#fff', borderRadius: 10, padding: 12, border: '1px solid #F0F0F0', textAlign: 'center' }}>
                  <div style={{ fontSize: 20, fontWeight: 700, color: s.color }}>{s.value}</div>
                  <div style={{ fontSize: 11, color: '#6B7280', marginTop: 2 }}>{s.label}</div>
                </div>
              ))}
            </div>

            {/* Onboarding data */}
            {selectedUser.onboardingData && Object.keys(selectedUser.onboardingData).length > 0 && (
              <div style={{ background: '#fff', borderRadius: 12, padding: 14, border: '1px solid #F0F0F0', marginBottom: 12 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#1F2937', marginBottom: 8 }}>Onboarding Data</div>
                {Object.entries(selectedUser.onboardingData).filter(([k]) => !['completedAt', 'totalStars', 'userType'].includes(k)).map(([k, v]) => (
                  <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px solid #F9FAFB', fontSize: 12 }}>
                    <span style={{ color: '#6B7280', textTransform: 'capitalize' }}>{k.replace(/([A-Z])/g, ' $1')}</span>
                    <span style={{ fontWeight: 600, color: '#374151' }}>{Array.isArray(v) ? v.join(', ') : String(v || '–')}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Dates */}
            <div style={{ background: '#fff', borderRadius: 12, padding: 14, border: '1px solid #F0F0F0', marginBottom: 16 }}>
              <div style={{ fontSize: 12, color: '#6B7280', display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span>Joined</span>
                <span style={{ fontWeight: 600, color: '#374151' }}>{selectedUser.created_at ? new Date(selectedUser.created_at).toLocaleDateString() : '–'}</span>
              </div>
              <div style={{ fontSize: 12, color: '#6B7280', display: 'flex', justifyContent: 'space-between' }}>
                <span>Last Updated</span>
                <span style={{ fontWeight: 600, color: '#374151' }}>{selectedUser.updated_at ? new Date(selectedUser.updated_at).toLocaleDateString() : '–'}</span>
              </div>
            </div>

            {/* Action buttons */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {/* Role management */}
              {selectedUser.id !== user?.id && (
                selectedUser.role === 'COMMUNITY_ADMIN' ? (
                  <button data-testid="demote-btn" onClick={() => handleDemoteUser(selectedUser.id)}
                    style={{ width: '100%', padding: '12px', borderRadius: 10, border: '1.5px solid #FDE68A', background: '#FFFBEB',
                      color: '#92400E', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                    Demote to User
                  </button>
                ) : (
                  <button data-testid="promote-btn" onClick={() => handlePromoteUser(selectedUser.id)}
                    style={{ width: '100%', padding: '12px', borderRadius: 10, border: '1.5px solid #C4B5FD', background: '#EDE9FE',
                      color: '#7C3AED', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                    Promote to Admin
                  </button>
                )
              )}
              {/* Block/unblock */}
              {selectedUser.role !== 'COMMUNITY_ADMIN' && (
                <button data-testid="block-toggle-btn"
                  onClick={() => { handleBlockUser(selectedUser.id, selectedUser.is_blocked); setSelectedUser(prev => prev ? { ...prev, is_blocked: !prev.is_blocked } : prev); }}
                  style={{ width: '100%', padding: '12px', borderRadius: 10, border: 'none',
                    background: selectedUser.is_blocked ? '#D1FAE5' : '#FEE2E2',
                    color: selectedUser.is_blocked ? '#047857' : '#DC2626',
                    fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                  {selectedUser.is_blocked ? 'Unblock User' : 'Block User'}
                </button>
              )}
            </div>
          </div>
        )}

        {/* Posts */}
        {activeTab === 'posts' && posts.map(p => (
          <div key={p.id} style={{ background: '#fff', borderRadius: 12, padding: 14, marginBottom: 8,
            border: '1px solid #F0F0F0', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: TEAL }}>{p.username}</div>
              <div style={{ fontSize: 13, color: '#374151', marginTop: 2 }}>{p.content?.slice(0, 100)}</div>
            </div>
            <button onClick={() => handleDeletePost(p.id)}
              style={{ background: '#FEF2F2', border: 'none', borderRadius: 8, padding: '6px 10px',
                cursor: 'pointer', color: '#DC2626', marginLeft: 10 }}>
              <Trash2 size={14} />
            </button>
          </div>
        ))}

        {/* Flagged Posts */}
        {activeTab === 'flagged' && (flaggedPosts.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#9CA3AF' }}>No flagged posts</div>
        ) : flaggedPosts.map(p => (
          <div key={p.id} style={{ background: '#FFF8F0', borderRadius: 12, padding: 14, marginBottom: 8,
            border: '1px solid #FDBA74' }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#F59E0B' }}>{p.username} (FLAGGED)</div>
            <div style={{ fontSize: 13, color: '#374151', marginTop: 2 }}>{p.content}</div>
            <button onClick={() => handleDeletePost(p.id)}
              style={{ marginTop: 8, padding: '6px 12px', background: '#DC2626', color: '#fff',
                border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
              Delete Post
            </button>
          </div>
        )))}

        {/* Events */}
        {activeTab === 'events' && events.map(ev => {
          const subCount = (ev.completion_submissions || []).length;
          const pendingCount = (ev.completion_submissions || []).filter(s => s.status === 'pending').length;
          return (
            <div key={ev.id} style={{ background: '#fff', borderRadius: 12, padding: 14, marginBottom: 8,
              border: '1px solid #F0F0F0' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#1F2937' }}>{ev.title}</div>
                  <div style={{ fontSize: 12, color: '#6B7280' }}>
                    {ev.attendees?.length || 0} RSVPs | VM: {ev.verification_method || 'manual'} | Stars: {ev.reward_stars || 0}
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button data-testid={`edit-event-${ev.id}`} onClick={() => setEditingEvent(ev)}
                  style={{ flex: 1, padding: '6px 10px', background: '#fff', color: '#374151', border: '1px solid #D1D5DB',
                    borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                  Edit
                </button>
                <button data-testid={`view-attendees-${ev.id}`} onClick={() => handleViewAttendees(ev.id)}
                  style={{ flex: 1, padding: '6px 10px', background: `${TEAL}15`, color: TEAL, border: `1px solid ${TEAL}`,
                    borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                  <Eye size={12} /> Attendees
                </button>
                {(ev.verification_method && ev.verification_method !== 'manual') && (
                  <button data-testid={`verify-${ev.id}`} onClick={() => handleViewCompletions(ev.id)}
                    style={{ flex: 1, padding: '6px 10px', background: pendingCount > 0 ? '#FEF3C7' : '#F0FDF4',
                      color: pendingCount > 0 ? '#92400E' : '#166534',
                      border: `1px solid ${pendingCount > 0 ? '#FCD34D' : '#BBF7D0'}`,
                      borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                    <CheckCircle size={12} />
                    Verify{pendingCount > 0 ? ` (${pendingCount})` : ''}
                  </button>
                )}
              </div>
            </div>
          );
        })}

        {/* Attendance */}
        {activeTab === 'attendance' && selectedEvent && (
          <div>
            <button onClick={() => setActiveTab('events')}
              style={{ background: 'none', border: 'none', color: TEAL, fontSize: 13, fontWeight: 600,
                cursor: 'pointer', marginBottom: 12, padding: 0 }}>
              &larr; Back to Events
            </button>
            <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}>{selectedEvent.title} - Attendees</h3>
            {attendees.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 40, color: '#9CA3AF' }}>No RSVPs yet</div>
            ) : attendees.map(a => (
              <div key={a.id} style={{ background: '#fff', borderRadius: 12, padding: 14, marginBottom: 8,
                border: '1px solid #F0F0F0' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#1F2937' }}>{a.username}</div>
                    <div style={{ fontSize: 12, color: '#6B7280' }}>{a.email}</div>
                    <div style={{ fontSize: 12, fontWeight: 600, marginTop: 2,
                      color: a.attendance_status === 'present' ? '#10B981' : a.attendance_status === 'absent' ? '#DC2626' : '#F59E0B' }}>
                      Status: {a.attendance_status || 'not_confirmed'}
                    </div>
                  </div>
                </div>
                {a.attendance_records?.map((r, i) => (
                  <div key={i} style={{ fontSize: 12, color: '#6B7280', padding: '4px 8px', background: '#F9FAFB',
                    borderRadius: 6, marginBottom: 4 }}>
                    {r.method && <>Method: {r.method} | {r.method === 'geo' ? `Lat: ${r.latitude}, Lng: ${r.longitude}` : r.image_url ? 'Photo uploaded' : ''}</>}
                    {r.group_size && (
                      <div data-testid={`attendee-group-${a.id}`} style={{ marginTop: 4, color: '#374151' }}>
                        Group of {r.group_size}{r.participant_names?.length ? `: ${r.participant_names.join(', ')}` : ''}
                      </div>
                    )}
                  </div>
                ))}
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <button onClick={() => handleMarkAttendance(selectedEvent.id, a.id, 'present')}
                    style={{ flex: 1, padding: '8px', borderRadius: 8, border: 'none', fontSize: 12, fontWeight: 600,
                      cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
                      background: a.attendance_status === 'present' ? '#10B981' : '#D1FAE5', color: a.attendance_status === 'present' ? '#fff' : '#047857' }}>
                    <CheckCircle size={12} /> Present
                  </button>
                  <button onClick={() => handleMarkAttendance(selectedEvent.id, a.id, 'absent')}
                    style={{ flex: 1, padding: '8px', borderRadius: 8, border: 'none', fontSize: 12, fontWeight: 600,
                      cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
                      background: a.attendance_status === 'absent' ? '#DC2626' : '#FEE2E2', color: a.attendance_status === 'absent' ? '#fff' : '#B91C1C' }}>
                    <XCircle size={12} /> Absent
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
        {/* Organisers — approval queue with Pending / Approved / Rejected sub-tabs */}
        {activeTab === 'organisers' && (
          <div data-testid="organiser-section">
            <div style={{ display: 'flex', gap: 6, marginBottom: 16, overflowX: 'auto' }} className="hide-scrollbar">
              {[
                { id: 'pending', label: 'Pending', count: organisers.pending.length },
                { id: 'approved', label: 'Approved', count: organisers.approved.length },
                { id: 'rejected', label: 'Rejected', count: organisers.rejected.length },
              ].map(st => (
                <button key={st.id} type="button" data-testid={`organiser-subtab-${st.id}`}
                  onClick={() => setOrganiserSubTab(st.id)}
                  style={{
                    padding: '8px 14px', borderRadius: 999,
                    border: `1.5px solid ${organiserSubTab === st.id ? TEAL : '#E5E7EB'}`,
                    background: organiserSubTab === st.id ? TEAL : '#FFF',
                    color: organiserSubTab === st.id ? '#FFF' : '#374151',
                    fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap',
                  }}>
                  {st.label}{st.count > 0 ? ` (${st.count})` : ''}
                </button>
              ))}
            </div>

            {(() => {
              const list = organisers[organiserSubTab] || [];
              if (list.length === 0) {
                return (
                  <div style={{ background: '#FFF', borderRadius: 12, padding: '40px 20px', textAlign: 'center', color: '#9CA3AF', fontSize: 14 }}>
                    {organiserSubTab === 'pending' ? 'No organiser requests waiting for review.' :
                     organiserSubTab === 'approved' ? 'No approved organisers yet.' :
                     'No rejected organisers.'}
                  </div>
                );
              }
              return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {list.map(org => (
                    <div key={org.id} data-testid={`organiser-card-${org.id}`}
                      style={{ background: '#FFF', borderRadius: 12, padding: 16, border: '1px solid #E5E7EB' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div style={{ width: 40, height: 40, borderRadius: 10, background: `${TEAL}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <Building2 size={20} color={TEAL} />
                          </div>
                          <div>
                            <div style={{ fontSize: 15, fontWeight: 700, color: '#1F2937' }}>{org.organisation_name || org.username}</div>
                            <div style={{ fontSize: 12, color: '#6B7280' }}>{org.email}</div>
                          </div>
                        </div>
                        <div style={{ fontSize: 11, color: '#9CA3AF', whiteSpace: 'nowrap' }}>
                          {org.created_at ? new Date(org.created_at).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : ''}
                        </div>
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8, marginTop: 10, marginBottom: 12 }}>
                        <Field label="Contact person" value={org.contact_person_name || '—'} />
                        <Field label="Alt. contact" value={org.alternative_contact_person_name || '—'} />
                        <Field label="Location" value={org.location || '—'} icon={<MapPin size={11} />} />
                        <Field label="Age group" value={org.age_group || '—'} />
                        <Field label="Outreach" value={typeof org.outreach === 'number' ? `${org.outreach} people` : '—'} />
                        <Field label="Status" value={(org.status || '').toUpperCase()} />
                      </div>

                      {org.objective && (
                        <div style={{ marginBottom: 12 }}>
                          <div style={{ fontSize: 10, fontWeight: 700, color: '#9CA3AF', letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 4 }}>Objective</div>
                          <p style={{ fontSize: 13, color: '#374151', lineHeight: 1.5, margin: 0, whiteSpace: 'pre-wrap' }}>{org.objective}</p>
                        </div>
                      )}

                      {organiserSubTab === 'pending' && (
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button type="button" data-testid={`approve-organiser-${org.id}`}
                            disabled={organiserActionLoading === org.id}
                            onClick={() => handleApproveOrganiser(org)}
                            style={{ flex: 1, padding: '10px', background: organiserActionLoading === org.id ? '#9CA3AF' : '#10B981', color: '#FFF', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: organiserActionLoading === org.id ? 'not-allowed' : 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                            <CheckCircle size={14} /> Approve
                          </button>
                          <button type="button" data-testid={`reject-organiser-${org.id}`}
                            disabled={organiserActionLoading === org.id}
                            onClick={() => handleRejectOrganiser(org)}
                            style={{ flex: 1, padding: '10px', background: '#FFF', color: '#DC2626', border: '1.5px solid #FCA5A5', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: organiserActionLoading === org.id ? 'not-allowed' : 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                            <XCircle size={14} /> Reject
                          </button>
                        </div>
                      )}
                      {organiserSubTab === 'approved' && (
                        <div style={{ fontSize: 12, color: '#10B981', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          <CheckCircle size={13} /> Approved{org.approved_at ? ` on ${new Date(org.approved_at).toLocaleDateString()}` : ''}
                        </div>
                      )}
                      {organiserSubTab === 'rejected' && (
                        <div style={{ fontSize: 12, color: '#DC2626', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          <XCircle size={13} /> Rejected{org.rejected_at ? ` on ${new Date(org.rejected_at).toLocaleDateString()}` : ''}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
        )}

        {/* Verify Completions */}
        {activeTab === 'verify' && (
          <div>
            {!completionEvent ? (
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 12 }}>Select an event from the Events tab to verify completions</div>
                <button onClick={() => setActiveTab('events')} style={{ padding: '8px 16px', background: TEAL, color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Go to Events</button>
              </div>
            ) : (
              <div>
                <button onClick={() => setActiveTab('events')}
                  style={{ background: 'none', border: 'none', color: TEAL, fontSize: 13, fontWeight: 600, cursor: 'pointer', marginBottom: 12, padding: 0 }}>
                  &larr; Back to Events
                </button>
                <div style={{ background: '#fff', borderRadius: 12, padding: 16, border: '1px solid #F0F0F0', marginBottom: 16 }}>
                  <h3 style={{ fontSize: 16, fontWeight: 600, color: '#1F2937', marginBottom: 4 }}>{completionEvent.event_title}</h3>
                  <div style={{ fontSize: 12, color: '#6B7280' }}>
                    Verification: {completionEvent.verification_method} | Reward: {completionEvent.reward_stars} stars | Submissions: {completions.length}
                  </div>
                </div>

                {completions.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: 40, color: '#9CA3AF' }}>No submissions yet</div>
                ) : completions.map(sub => (
                  <div key={sub.user_id} data-testid={`completion-${sub.user_id}`}
                    style={{ background: '#fff', borderRadius: 12, padding: 14, marginBottom: 10,
                      border: `1px solid ${sub.status === 'approved' ? '#BBF7D0' : sub.status === 'rejected' ? '#FECACA' : '#FDE68A'}` }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 8 }}>
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 600, color: '#1F2937' }}>{sub.username}</div>
                        <div style={{ fontSize: 12, color: '#6B7280' }}>Submitted: {new Date(sub.submitted_at).toLocaleString()}</div>
                        <div style={{ fontSize: 12, fontWeight: 600, marginTop: 4,
                          color: sub.status === 'approved' ? '#047857' : sub.status === 'rejected' ? '#B91C1C' : '#92400E' }}>
                          {sub.status === 'pending' ? 'Pending Review' : sub.status === 'approved' ? 'Approved' : 'Rejected'}
                        </div>
                      </div>
                    </div>

                    {/* Photo thumbnail */}
                    {sub.photo_url && (
                      <div style={{ marginBottom: 8 }}>
                        <img src={sub.photo_url.startsWith('/') ? `${API.replace('/api', '')}${sub.photo_url}` : sub.photo_url}
                          onError={(e) => { e.target.style.display = 'none'; }}
                          alt="Completion" style={{ width: '100%', maxHeight: 160, objectFit: 'cover', borderRadius: 8, border: '1px solid #E5E7EB' }} />
                      </div>
                    )}

                    {/* Geo coords */}
                    {sub.geo_coords && (
                      <div style={{ fontSize: 12, color: '#6B7280', padding: '6px 10px', background: '#F9FAFB', borderRadius: 6, marginBottom: 8 }}>
                        Location: {sub.geo_coords.latitude?.toFixed(4)}, {sub.geo_coords.longitude?.toFixed(4)}
                      </div>
                    )}

                    {/* Action buttons for pending */}
                    {sub.status === 'pending' && (
                      <div>
                        {rejectingUserId === sub.user_id ? (
                          <div style={{ marginBottom: 8 }}>
                            <textarea value={rejectReason} onChange={e => setRejectReason(e.target.value.slice(0, 200))}
                              placeholder="Rejection reason (optional, max 200 chars)"
                              style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1.5px solid #FECACA', fontSize: 12, resize: 'none', boxSizing: 'border-box' }}
                              rows={2} maxLength={200} data-testid={`reject-reason-${sub.user_id}`} />
                            <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                              <button onClick={() => handleRejectCompletion(sub.user_id)} data-testid={`confirm-reject-${sub.user_id}`}
                                style={{ flex: 1, padding: '8px', borderRadius: 8, border: 'none', background: '#DC2626', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                                Confirm Reject
                              </button>
                              <button onClick={() => { setRejectingUserId(null); setRejectReason(''); }}
                                style={{ flex: 1, padding: '8px', borderRadius: 8, border: '1px solid #E5E7EB', background: '#fff', color: '#6B7280', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div style={{ display: 'flex', gap: 8 }}>
                            <button onClick={() => handleApproveCompletion(sub.user_id)} data-testid={`approve-${sub.user_id}`}
                              style={{ flex: 1, padding: '8px', borderRadius: 8, border: 'none', background: '#10B981', color: '#fff',
                                fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                              <CheckCircle size={12} /> Approve
                            </button>
                            <button onClick={() => setRejectingUserId(sub.user_id)} data-testid={`reject-${sub.user_id}`}
                              style={{ flex: 1, padding: '8px', borderRadius: 8, border: 'none', background: '#DC2626', color: '#fff',
                                fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                              <XCircle size={12} /> Reject
                            </button>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Rejection reason display */}
                    {sub.status === 'rejected' && sub.rejection_reason && (
                      <div style={{ fontSize: 11, color: '#B91C1C', padding: '4px 8px', background: '#FEF2F2', borderRadius: 4 }}>
                        Reason: {sub.rejection_reason}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Announcements Tab ── */}
        {activeTab === 'announcements' && (
          <div>
            {!showCreateAnn ? (
              <>
                <button data-testid="create-announcement-btn" onClick={() => setShowCreateAnn(true)}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, width: '100%', padding: '12px', borderRadius: 10, border: `1.5px dashed ${TEAL}`, background: '#f0faf9', color: TEAL, fontSize: 14, fontWeight: 600, cursor: 'pointer', marginBottom: 16 }}>
                  <Plus size={16} /> Create Announcement
                </button>
                {announcements.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: 40, color: '#9CA3AF' }}>No announcements yet</div>
                ) : announcements.map(a => (
                  <div key={a.id} data-testid={`admin-ann-${a.id}`} style={{ background: '#fff', borderRadius: 12, padding: 14, marginBottom: 10, border: '1px solid #F0F0F0', display: 'flex', alignItems: 'center', gap: 12 }}>
                    {a.image_url ? (
                      <img src={a.image_url} alt="" style={{ width: 48, height: 48, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }} />
                    ) : (
                      <div style={{ width: 48, height: 48, borderRadius: 8, background: '#E0F2F1', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <Megaphone size={20} color={TEAL} />
                      </div>
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: '#1F2937' }}>{a.title}</div>
                      <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 2 }}>{new Date(a.created_at).toLocaleDateString()}</div>
                    </div>
                    <button onClick={async () => {
                      if (!(await confirmDialog({ title: 'Delete announcement?', confirmText: 'Delete', danger: true }))) return;
                      try { await axios.delete(`${API}/community/announcements/${a.id}`, authHeaders); setAnnouncements(prev => prev.filter(x => x.id !== a.id)); } catch { alert('Failed'); }
                    }} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 6 }}>
                      <Trash2 size={16} color="#EF4444" />
                    </button>
                  </div>
                ))}
              </>
            ) : (
              <div style={{ background: '#fff', borderRadius: 14, padding: 20, border: '1px solid #F0F0F0' }}>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#1F2937', marginBottom: 16 }}>New Announcement</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 600, color: '#6B7280', marginBottom: 6, display: 'block' }}>Title *</label>
                    <input data-testid="ann-title" value={annForm.title} onChange={e => setAnnForm(f => ({ ...f, title: e.target.value }))}
                      style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #E5E7EB', fontSize: 14, outline: 'none', boxSizing: 'border-box' }} placeholder="Announcement title" />
                  </div>
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 600, color: '#6B7280', marginBottom: 6, display: 'block' }}>Description</label>
                    <textarea data-testid="ann-description" value={annForm.description} onChange={e => setAnnForm(f => ({ ...f, description: e.target.value }))} rows={3}
                      style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #E5E7EB', fontSize: 14, outline: 'none', resize: 'vertical', boxSizing: 'border-box' }} placeholder="Details..." />
                  </div>
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 600, color: '#6B7280', marginBottom: 6, display: 'block' }}>Image (optional)</label>
                    {annImagePreview ? (
                      <div style={{ position: 'relative', marginBottom: 8 }}>
                        <img src={annImagePreview} alt="" style={{ width: '100%', height: 120, objectFit: 'cover', borderRadius: 10 }} />
                        <button onClick={() => { setAnnImageFile(null); setAnnImagePreview(null); }}
                          style={{ position: 'absolute', top: 6, right: 6, width: 24, height: 24, borderRadius: '50%', background: 'rgba(0,0,0,0.6)', border: 'none', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: 14 }}>×</button>
                      </div>
                    ) : (
                      <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '12px', borderRadius: 8, border: '1.5px dashed #D1D5DB', cursor: 'pointer', color: '#6B7280', fontSize: 13 }}>
                        <ImagePlus size={16} /> Upload image
                        <input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          setAnnImageFile(file);
                          const reader = new FileReader();
                          reader.onloadend = () => setAnnImagePreview(reader.result);
                          reader.readAsDataURL(file);
                        }} />
                      </label>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
                    <button onClick={() => { setShowCreateAnn(false); setAnnForm({ title: '', description: '', image_url: '' }); setAnnImageFile(null); setAnnImagePreview(null); }}
                      style={{ flex: 1, padding: '10px', borderRadius: 8, border: '1px solid #E5E7EB', background: '#F9FAFB', color: '#374151', fontSize: 14, fontWeight: 500, cursor: 'pointer' }}>Cancel</button>
                    <button data-testid="ann-publish-btn" disabled={annSubmitting || !annForm.title.trim()}
                      onClick={async () => {
                        setAnnSubmitting(true);
                        try {
                          let image_url = null;
                          if (annImageFile) {
                            const fd = new FormData(); fd.append('file', annImageFile);
                            const upRes = await axios.post(`${API}/community/upload`, fd, { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } });
                            image_url = upRes.data.url;
                          }
                          const res = await axios.post(`${API}/community/announcements`, { title: annForm.title, description: annForm.description, image_url }, authHeaders);
                          setAnnouncements(prev => [res.data, ...prev]);
                          setShowCreateAnn(false); setAnnForm({ title: '', description: '', image_url: '' }); setAnnImageFile(null); setAnnImagePreview(null);
                        } catch (err) { alert(err.response?.data?.detail || 'Failed to create'); }
                        finally { setAnnSubmitting(false); }
                      }}
                      style={{ flex: 1, padding: '10px', borderRadius: 8, border: 'none', background: !annForm.title.trim() ? '#D0D0D0' : TEAL, color: '#fff', fontSize: 14, fontWeight: 600, cursor: annSubmitting ? 'not-allowed' : 'pointer' }}>
                      {annSubmitting ? 'Publishing...' : 'Publish'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── MAD Actions list (with soft-delete toggle) ── */}
        {activeTab === 'actions' && (
          <div>
            {allChallenges.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 40, color: '#9CA3AF' }}>No MAD actions yet</div>
            ) : allChallenges.filter(ch => ch.community_approval_status !== 'pending' && ch.community_approval_status !== 'rejected').map(ch => {
              const pendingSubs = (ch.completion_submissions || []).filter(s => s.status === 'pending').length;
              const isActive = ch.is_active !== false;
              return (
                <div key={ch.id} style={{ background: '#fff', borderRadius: 12, padding: 14, marginBottom: 8,
                  border: `1px solid ${isActive ? '#F0F0F0' : '#FECACA'}`, opacity: isActive ? 1 : 0.65 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: '#1F2937', display: 'flex', alignItems: 'center', gap: 6 }}>
                        {ch.title}
                        {!isActive && <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 4, background: '#FEE2E2', color: '#DC2626' }}>HIDDEN</span>}
                        {ch.created_by_org_name && <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 4, background: '#DBEAFE', color: '#2563EB' }}>ORG</span>}
                      </div>
                      <div style={{ fontSize: 12, color: '#6B7280' }}>
                        {ch.participants?.length || 0} joined | {ch.geo_tag_required ? 'Geo-tagged' : 'Standard'} | +{ch.reward_points} pts
                      </div>
                    </div>
                    {/* Active/Inactive toggle */}
                    <button data-testid={`toggle-active-${ch.id}`} onClick={() => handleToggleChallengeActive(ch.id)}
                      style={{
                        width: 46, height: 26, borderRadius: 13, border: 'none', cursor: 'pointer',
                        background: isActive ? '#10B981' : '#D1D5DB', position: 'relative', transition: 'background 0.2s', flexShrink: 0,
                      }}>
                      <div style={{
                        width: 20, height: 20, borderRadius: '50%', background: '#fff', position: 'absolute', top: 3,
                        left: isActive ? 23 : 3, transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
                      }} />
                    </button>
                  </div>
                  {ch.geo_tag_required && (
                    <button data-testid={`verify-challenge-${ch.id}`} onClick={() => handleViewChallengeCompletions(ch.id)}
                      style={{
                        width: '100%', padding: '8px 10px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
                        background: pendingSubs > 0 ? '#FEF3C7' : '#F0FDF4',
                        color: pendingSubs > 0 ? '#92400E' : '#166534',
                        border: `1px solid ${pendingSubs > 0 ? '#FCD34D' : '#BBF7D0'}`,
                      }}>
                      <MapPin size={12} /> Verify Geo{pendingSubs > 0 ? ` (${pendingSubs} pending)` : ''}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* ── Pending Org-Submitted Actions (with bulk) ── */}
        {activeTab === 'pending' && (
          <div>
            {pendingActions.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 40, color: '#9CA3AF' }}>No pending actions from organisations</div>
            ) : (
              <>
                {/* Bulk action bar */}
                {selectedChecked.length > 0 && (
                  <div data-testid="bulk-action-bar" style={{
                    position: 'sticky', top: 0, zIndex: 10, display: 'flex', alignItems: 'center', gap: 8,
                    padding: '10px 14px', background: '#FEF3C7', border: '1px solid #FCD34D',
                    borderRadius: 10, marginBottom: 12,
                  }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#92400E', flex: 1 }}>
                      {selectedChecked.length} selected
                    </span>
                    {showBulkReject ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: 2 }}>
                        <textarea value={bulkRejectReason} onChange={e => setBulkRejectReason(e.target.value.slice(0, 200))}
                          placeholder="Rejection reason (optional)"
                          style={{ width: '100%', padding: '6px 10px', borderRadius: 6, border: '1px solid #FECACA', fontSize: 12, resize: 'none', boxSizing: 'border-box' }}
                          rows={2} maxLength={200} />
                        <div style={{ display: 'flex', gap: 4 }}>
                          <button data-testid="confirm-bulk-reject" onClick={handleBulkReject}
                            style={{ flex: 1, padding: '6px', borderRadius: 6, border: 'none', background: '#DC2626', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                            Reject
                          </button>
                          <button onClick={() => setShowBulkReject(false)}
                            style={{ flex: 1, padding: '6px', borderRadius: 6, border: '1px solid #E5E7EB', background: '#fff', color: '#6B7280', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <button data-testid="bulk-approve-btn" onClick={handleBulkApprove}
                          style={{ padding: '6px 14px', borderRadius: 6, border: 'none', background: '#10B981', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                          Approve Selected
                        </button>
                        <button data-testid="bulk-reject-btn" onClick={() => setShowBulkReject(true)}
                          style={{ padding: '6px 14px', borderRadius: 6, border: 'none', background: '#DC2626', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                          Reject Selected
                        </button>
                      </>
                    )}
                  </div>
                )}

                {/* Select all checkbox */}
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600, color: '#6B7280', marginBottom: 10, cursor: 'pointer' }}>
                  <input type="checkbox" checked={selectedChecked.length === pendingActions.length && pendingActions.length > 0}
                    onChange={toggleAllChecked} style={{ width: 16, height: 16, accentColor: TEAL }} />
                  Select all ({pendingActions.length})
                </label>

                {pendingActions.map(action => (
                  <div key={action.id} data-testid={`pending-action-${action.id}`}
                    style={{ background: '#fff', borderRadius: 12, padding: 16, marginBottom: 10, border: '1px solid #FDE68A' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 8 }}>
                      <input type="checkbox" checked={selectedChecked.includes(action.id)}
                        onChange={() => toggleChecked(action.id)}
                        data-testid={`check-${action.id}`}
                        style={{ marginTop: 2, width: 16, height: 16, accentColor: TEAL, flexShrink: 0 }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                          <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4, background: '#FEF3C7', color: '#92400E' }}>Org Submitted</span>
                        </div>
                        <div style={{ fontSize: 15, fontWeight: 700, color: '#1F2937', marginBottom: 3 }}>{action.title}</div>
                        <div style={{ fontSize: 13, color: '#6B7280', lineHeight: 1.5, marginBottom: 6 }}>{action.description}</div>
                        <div style={{ fontSize: 12, color: '#6B7280', display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                          <span style={{ fontWeight: 600, color: TEAL }}>Organiser: {action.created_by_org_name || 'Unknown'}</span>
                          <span>+{action.reward_points} pts</span>
                          {action.city && <span>{action.city}</span>}
                        </div>
                      </div>
                    </div>

                    {/* Per-row approve/reject */}
                    {pendingRejectId === action.id ? (
                      <div style={{ marginLeft: 26 }}>
                        <textarea value={pendingRejectReason} onChange={e => setPendingRejectReason(e.target.value.slice(0, 200))}
                          placeholder="Rejection reason (optional)"
                          style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1.5px solid #FECACA', fontSize: 12, resize: 'none', boxSizing: 'border-box', marginBottom: 6 }}
                          rows={2} maxLength={200} />
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button onClick={() => handleRejectPendingAction(action.id)}
                            style={{ flex: 1, padding: '8px', borderRadius: 8, border: 'none', background: '#DC2626', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                            Confirm Reject
                          </button>
                          <button onClick={() => { setPendingRejectId(null); setPendingRejectReason(''); }}
                            style={{ flex: 1, padding: '8px', borderRadius: 8, border: '1px solid #E5E7EB', background: '#fff', color: '#6B7280', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', gap: 8, marginLeft: 26 }}>
                        <button onClick={() => handleApprovePendingAction(action.id)} data-testid={`approve-pending-${action.id}`}
                          style={{ flex: 1, padding: '10px', borderRadius: 8, border: 'none', background: '#10B981', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                          <CheckCircle size={14} /> Approve
                        </button>
                        <button onClick={() => setPendingRejectId(action.id)} data-testid={`reject-pending-${action.id}`}
                          style={{ flex: 1, padding: '10px', borderRadius: 8, border: 'none', background: '#DC2626', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                          <XCircle size={14} /> Reject
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </>
            )}
          </div>
        )}

        {/* ── Verify Challenge Geo-Submissions ── */}
        {activeTab === 'verify-challenge' && (
          <div>
            {!completionChallenge ? (
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 12 }}>Select an action from the Actions tab</div>
                <button onClick={() => setActiveTab('actions')} style={{ padding: '8px 16px', background: TEAL, color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Go to Actions</button>
              </div>
            ) : (
              <div>
                <button onClick={() => setActiveTab('actions')}
                  style={{ background: 'none', border: 'none', color: TEAL, fontSize: 13, fontWeight: 600, cursor: 'pointer', marginBottom: 12, padding: 0 }}>
                  &larr; Back to Actions
                </button>
                <div style={{ background: '#fff', borderRadius: 12, padding: 16, border: '1px solid #F0F0F0', marginBottom: 16 }}>
                  <h3 style={{ fontSize: 16, fontWeight: 600, color: '#1F2937', marginBottom: 4 }}>{completionChallenge.challenge_title}</h3>
                  <div style={{ fontSize: 12, color: '#6B7280' }}>
                    Geo-tagged | Reward: {completionChallenge.reward_points} pts | Submissions: {challengeCompletions.length}
                  </div>
                </div>

                {challengeCompletions.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: 40, color: '#9CA3AF' }}>No geo-submissions yet</div>
                ) : challengeCompletions.map(sub => (
                  <div key={sub.userId} data-testid={`challenge-sub-${sub.userId}`}
                    style={{ background: '#fff', borderRadius: 12, padding: 14, marginBottom: 10,
                      border: `1px solid ${sub.status === 'approved' ? '#BBF7D0' : sub.status === 'rejected' ? '#FECACA' : '#FDE68A'}` }}>
                    <div style={{ marginBottom: 8 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: '#1F2937' }}>{sub.username || sub.userId}</div>
                      <div style={{ fontSize: 12, color: '#6B7280' }}>Submitted: {new Date(sub.timestamp).toLocaleString()}</div>
                      <div style={{ fontSize: 12, fontWeight: 600, marginTop: 4,
                        color: sub.status === 'approved' ? '#047857' : sub.status === 'rejected' ? '#B91C1C' : '#92400E' }}>
                        {sub.status === 'pending' ? 'Pending Review' : sub.status === 'approved' ? 'Approved' : 'Rejected'}
                      </div>
                    </div>

                    {/* Location info + Google Maps link */}
                    {sub.latitude != null && sub.longitude != null && (
                      <div style={{ padding: '8px 12px', background: '#F9FAFB', borderRadius: 8, marginBottom: 8 }}>
                        <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 4 }}>
                          Lat: {sub.latitude.toFixed(5)}, Lng: {sub.longitude.toFixed(5)}
                          {sub.accuracy != null && <span> (±{Math.round(sub.accuracy)}m)</span>}
                        </div>
                        <a data-testid={`maps-link-${sub.userId}`}
                          href={`https://maps.google.com/?q=${sub.latitude},${sub.longitude}`}
                          target="_blank" rel="noopener noreferrer"
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12,
                            fontWeight: 600, color: '#4E9FFF', textDecoration: 'none' }}>
                          <ExternalLink size={12} /> View on Google Maps
                        </a>
                      </div>
                    )}

                    {/* Approve/Reject for pending */}
                    {sub.status === 'pending' && (
                      <div>
                        {challengeRejectUserId === sub.userId ? (
                          <div style={{ marginBottom: 8 }}>
                            <textarea value={challengeRejectReason} onChange={e => setChallengeRejectReason(e.target.value.slice(0, 200))}
                              placeholder="Rejection reason (optional)"
                              style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1.5px solid #FECACA', fontSize: 12, resize: 'none', boxSizing: 'border-box' }}
                              rows={2} maxLength={200} />
                            <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                              <button onClick={() => handleRejectChallengeCompletion(sub.userId)}
                                style={{ flex: 1, padding: '8px', borderRadius: 8, border: 'none', background: '#DC2626', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                                Confirm Reject
                              </button>
                              <button onClick={() => { setChallengeRejectUserId(null); setChallengeRejectReason(''); }}
                                style={{ flex: 1, padding: '8px', borderRadius: 8, border: '1px solid #E5E7EB', background: '#fff', color: '#6B7280', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div style={{ display: 'flex', gap: 8 }}>
                            <button onClick={() => handleApproveChallengeCompletion(sub.userId)}
                              data-testid={`approve-challenge-${sub.userId}`}
                              style={{ flex: 1, padding: '8px', borderRadius: 8, border: 'none', background: '#10B981', color: '#fff',
                                fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                              <CheckCircle size={12} /> Approve
                            </button>
                            <button onClick={() => setChallengeRejectUserId(sub.userId)}
                              data-testid={`reject-challenge-${sub.userId}`}
                              style={{ flex: 1, padding: '8px', borderRadius: 8, border: 'none', background: '#DC2626', color: '#fff',
                                fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                              <XCircle size={12} /> Reject
                            </button>
                          </div>
                        )}
                      </div>
                    )}

                    {sub.status === 'rejected' && sub.rejection_reason && (
                      <div style={{ fontSize: 11, color: '#B91C1C', padding: '4px 8px', background: '#FEF2F2', borderRadius: 4 }}>
                        Reason: {sub.rejection_reason}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

      </div>

      <CommunityBottomNav active="menu" />
    </div>
  );
}

// Compact label/value pair used in the organiser request cards.
function Field({ label, value, icon }) {
  return (
    <div>
      <div style={{ fontSize: 10, fontWeight: 700, color: '#9CA3AF', letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 2, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        {icon}{label}
      </div>
      <div style={{ fontSize: 13, color: '#1F2937', fontWeight: 500 }}>{value}</div>
    </div>
  );
}
